import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Menu, X, LogOut, Settings, Package,
  ChevronDown, Heart, GitCompare, RotateCcw,
  Home, Store, Info, Sun, ShoppingCart
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import { useCompare } from "../../context/CompareContext";
import SearchBar from "../search/SearchBar";
import { CATEGORY_GROUPS } from "../../utils/format";
import toast from "react-hot-toast";

const PROMO_MESSAGES = [
  "🚚 Reliable door delivery & station pickup options nationwide — guaranteed within 7 days",
  "🔋 Solar Inverters & Lithium batteries in stock — calculate your load now!",
  "💳 Secured payments via Paystack — 100% safe checkout",
  "⭐ 30-day hassle-free returns on all authentic products",
];

const ROLE_CONFIG = {
  admin:     { label: "Super Admin", color: "#DC2626", bg: "#FEE2E2" },
  web_admin: { label: "Web Admin",   color: "#D97706", bg: "#FEF3C7" },
  accounts:  { label: "Accounts",    color: "#16A34A", bg: "#DCFCE7" },
  business:  { label: "Business",    color: "#2563EB", bg: "#DBEAFE" },
  customer:  { label: "Customer",    color: "#64748B", bg: "#F1F5F9" },
};

export default function Navbar() {
  const { user, profile, logout, isStaff } = useAuth();
  const { count } = useCart();
  const { wishlist } = useWishlist();
  const { compareList } = useCompare();
  const navigate = useNavigate();
  const location = useLocation();

  const [promoIdx, setPromoIdx]     = useState(0);
  const [promoAnim, setPromoAnim]   = useState(true);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen]     = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const megaTimer = useRef(null);

  // Scroll shadow
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Promo bar rotation
  useEffect(() => {
    const id = setInterval(() => {
      setPromoAnim(false);
      setTimeout(() => {
        setPromoIdx(i => (i + 1) % PROMO_MESSAGES.length);
        setPromoAnim(true);
      }, 300);
    }, 3500);
    return () => clearInterval(id);
  }, []);

  // Close mobile menu on navigate
  useEffect(() => { setMobileOpen(false); setMenuOpen(false); }, [location.pathname]);

  async function handleLogout() {
    await logout();
    toast.success("Signed out successfully");
    navigate("/");
  }

  const roleConfig = ROLE_CONFIG[profile?.role] || ROLE_CONFIG.customer;

  return (
    <>
      <header
        style={{
          position: "sticky", top: 0, zIndex: 200,
          background: scrolled ? "rgba(255,255,255,0.95)" : "#fff",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: "1px solid var(--border)",
          boxShadow: scrolled ? "0 4px 24px rgba(13,27,42,0.08)" : "none",
          transition: "all 0.3s var(--ease)",
        }}
      >
        {/* ── Promo Bar ─────────────────────────────────────────────────── */}
        <div style={{
          background: "linear-gradient(90deg, #0D1B2A 0%, #162436 50%, #1E3148 100%)",
          color: "#fff", fontSize: 13, padding: "8px 12px", textAlign: "center",
          overflow: "hidden", position: "relative",
        }}>
          <div style={{
            opacity: promoAnim ? 1 : 0,
            transform: promoAnim ? "translateY(0)" : "translateY(-8px)",
            transition: "all 0.3s var(--ease)",
            fontWeight: 500, letterSpacing: "0.01em",
            wordBreak: "break-word", overflowWrap: "break-word",
          }}>
            {PROMO_MESSAGES[promoIdx]}
          </div>
        </div>

        {/* ── Main Nav Row ───────────────────────────────────────────────── */}
        <div className="container" style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 clamp(14px, 3vw, 28px)", height: 66 }}>

          {/* Logo */}
          <Link to="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
            <img src="/images/Afriportstore.png" alt="Afriport Logo" id="logo-img" style={{ height: "clamp(36px, 5vw, 48px)", width: "auto", objectFit: "contain" }} onError={e => { e.currentTarget.style.display = 'none'; }} />
          </Link>

          {/* Category mega-menu trigger (desktop) */}
          <div
            style={{ position: "relative", flexShrink: 0 }}
            className="hide-mobile"
            onMouseEnter={() => { clearTimeout(megaTimer.current); setMegaOpen(true); }}
            onMouseLeave={() => { megaTimer.current = setTimeout(() => setMegaOpen(false), 180); }}
          >
            <button style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 14px", borderRadius: "var(--radius-sm)",
              border: "none", background: megaOpen ? "var(--brand-light)" : "transparent",
              color: megaOpen ? "var(--brand)" : "var(--text)",
              fontSize: 14, fontWeight: 600, cursor: "pointer",
              transition: "all 0.2s",
            }}>
              <Store size={16} />
              Shop
              <ChevronDown size={14} style={{ transform: megaOpen ? "rotate(180deg)" : "", transition: "transform 0.2s" }} />
            </button>

            {/* Mega Menu */}
            {megaOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 10px)", left: "50%",
                transform: "translateX(-38%)",
                background: "#fff",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
                boxShadow: "var(--shadow-lg)",
                padding: "24px 28px", zIndex: 300,
                display: "grid", gridTemplateColumns: "repeat(3, 270px)",
                gap: "24px 28px",
                maxHeight: "82vh",
                overflowY: "auto",
                animation: "fadeUp 0.18s var(--ease)",
              }}>
                {CATEGORY_GROUPS.map(group => (
                  <div key={group.group} style={{ display: "flex", flexDirection: "column" }}>
                    <Link
                      to={`/shop?category=${encodeURIComponent(group.group)}`}
                      onClick={() => setMegaOpen(false)}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "6px 8px", borderRadius: "var(--radius-sm)",
                        textDecoration: "none", color: "var(--brand)",
                        fontWeight: 800, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.05em",
                        borderBottom: "1.5px solid var(--border)", marginBottom: 8,
                        transition: "background 0.15s"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--brand-light)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 16 }}>{group.icon}</span>
                        {group.group}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>All &rarr;</span>
                    </Link>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {group.items.map(item => (
                        <Link key={item.name}
                          to={`/shop?category=${encodeURIComponent(group.group)}&subcategory=${encodeURIComponent(item.name)}`}
                          onClick={() => setMegaOpen(false)}
                          style={{
                            display: "flex", alignItems: "center", gap: 8,
                            padding: "6px 8px", borderRadius: "var(--radius-sm)",
                            transition: "background 0.15s", textDecoration: "none",
                            color: "var(--text)"
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.color = "var(--brand)"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text)"; }}
                        >
                          <span style={{ fontSize: 14 }}>{item.icon}</span>
                          <span style={{ fontSize: 13, fontWeight: 500, transition: "color 0.15s" }}>
                            {item.name}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
                <div style={{ gridColumn: "1 / -1", borderTop: "1px solid var(--border)", marginTop: 8, paddingTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Explore all 9 major departments & 33 sub-categories</span>
                  <Link to="/shop" onClick={() => setMegaOpen(false)} style={{ fontSize: 13, fontWeight: 700, color: "var(--brand)", display: "flex", alignItems: "center", gap: 4 }}>
                    View All Products →
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Search bar (desktop) */}
          <div style={{ flex: 1, maxWidth: 500 }} className="hide-mobile">
            <SearchBar />
          </div>

          {/* Right icons */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto", flexShrink: 0 }}>

            {/* Wishlist */}
            {user && (
              <Link to="/wishlist" title="Wishlist" className="hide-mobile"
                style={{ position: "relative", display: "flex", padding: 9, borderRadius: "var(--radius-sm)", color: "var(--text)", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.color = "var(--brand)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.color = "var(--text)"; }}
              >
                <Heart size={21} />
                {wishlist.length > 0 && (
                  <span style={{
                    position: "absolute", top: 4, right: 4,
                    background: "var(--danger)", color: "#fff",
                    fontSize: 10, fontWeight: 700, minWidth: 16, height: 16,
                    borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                    animation: "pulse-dot 2s infinite",
                  }}>{wishlist.length}</span>
                )}
              </Link>
            )}

            {/* Compare */}
            {compareList.length > 0 && (
              <Link to="/compare" title="Compare" className="hide-mobile"
                style={{ position: "relative", display: "flex", padding: 9, borderRadius: "var(--radius-sm)", color: "var(--brand)", transition: "all 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--brand-light)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}
              >
                <GitCompare size={21} />
                <span style={{
                  position: "absolute", top: 4, right: 4,
                  background: "var(--brand)", color: "#fff",
                  fontSize: 10, fontWeight: 700, minWidth: 16, height: 16,
                  borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                }}>{compareList.length}</span>
              </Link>
            )}

            {/* Cart */}
            <Link to="/cart" className="cart-icon" id="cart-icon"
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 12px",
                borderRadius: "var(--radius-full)",
                color: "var(--text)",
                background: "var(--surface)",
                border: "1px solid var(--border)",
                fontWeight: 700,
                fontSize: 13,
                transition: "all 0.2s",
                textDecoration: "none"
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--brand-light)"; e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.color = "var(--brand)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--text)"; }}
            >
              <ShoppingCart size={18} />
              <span className="hide-mobile">Cart</span>
              <span id="cart-count" style={{
                background: count > 0 ? "var(--brand)" : "var(--border)",
                color: count > 0 ? "#fff" : "var(--text-muted)",
                fontSize: 11,
                fontWeight: 700,
                minWidth: 18,
                height: 18,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "0 4px"
              }}>{count}</span>
            </Link>

            {/* Auth */}
            {user ? (
              <div style={{ position: "relative" }} className="hide-mobile">
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 12px 7px 8px",
                    border: menuOpen ? "1.5px solid var(--brand)" : "1.5px solid var(--border)",
                    borderRadius: "var(--radius-full)", background: menuOpen ? "var(--brand-light)" : "#fff",
                    fontSize: 14, cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  <div style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: "linear-gradient(135deg, var(--brand), #FF6B35)",
                    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 700, flexShrink: 0,
                  }}>
                    {profile?.fullName?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span style={{ fontWeight: 600, maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} className="hide-mobile">
                    {profile?.fullName?.split(" ")[0] || "Account"}
                  </span>
                  <ChevronDown size={13} style={{ transform: menuOpen ? "rotate(180deg)" : "", transition: "transform 0.2s", color: "var(--text-muted)" }} />
                </button>

                {menuOpen && (
                  <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 150 }} onClick={() => setMenuOpen(false)} />
                    <div style={{
                      position: "absolute", right: 0, top: "calc(100% + 10px)",
                      background: "#fff", border: "1px solid var(--border)",
                      borderRadius: "var(--radius-md)", boxShadow: "var(--shadow-lg)",
                      minWidth: 220, padding: 8, zIndex: 200,
                      animation: "fadeUp 0.18s var(--ease)",
                    }}>
                      {/* User info */}
                      <div style={{ padding: "12px 14px 14px", borderBottom: "1px solid var(--border)", marginBottom: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{
                            width: 40, height: 40, borderRadius: "50%",
                            background: "linear-gradient(135deg, var(--brand), #FF6B35)",
                            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 16, fontWeight: 700,
                          }}>
                            {profile?.fullName?.[0]?.toUpperCase() || "U"}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>{profile?.fullName || "—"}</div>
                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 1 }}>{profile?.email}</div>
                          </div>
                        </div>
                        <span style={{
                          display: "inline-flex", alignItems: "center", marginTop: 10,
                          padding: "2px 10px", borderRadius: "var(--radius-full)",
                          fontSize: 11, fontWeight: 600,
                          background: roleConfig.bg, color: roleConfig.color,
                        }}>
                          {roleConfig.label}
                        </span>
                      </div>

                      {/* Menu items */}
                      {[
                        ["/account/orders", Package,   "My Orders",       "var(--text)"],
                        ["/returns",        RotateCcw, "Returns",          "var(--text)"],
                        ["/wishlist",       Heart,     "Wishlist",          "var(--text)"],
                        ...(isStaff ? [["/admin", Settings, "Admin Panel", "var(--brand)"]] : []),
                        ...(profile?.role === "business" ? [
                          ["/business/dashboard", Package, "Seller Dashboard", "#2563EB"],
                          ["/business/submit",    Package, "Submit Product",    "#2563EB"],
                        ] : []),
                      ].map(([href, Icon, label, color]) => (
                        <Link key={href} to={href} onClick={() => setMenuOpen(false)}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "9px 14px", fontSize: 14, borderRadius: "var(--radius-sm)",
                            color, transition: "background 0.15s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          <Icon size={15} /> {label}
                        </Link>
                      ))}

                      <div style={{ borderTop: "1px solid var(--border)", marginTop: 4, paddingTop: 4 }}>
                        <button
                          onClick={handleLogout}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "9px 14px", fontSize: 14, borderRadius: "var(--radius-sm)",
                            color: "var(--danger)", background: "transparent",
                            border: "none", width: "100%", cursor: "pointer",
                            transition: "background 0.15s",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--danger-bg)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          <LogOut size={15} /> Sign Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div style={{ display: "flex", gap: 8, marginLeft: 4 }} className="hide-mobile">
                <Link to="/login" className="btn btn-ghost btn-sm">Sign In</Link>
                <Link to="/register" className="btn btn-primary btn-sm">Register</Link>
              </div>
            )}

            {/* Mobile hamburger */}
            <button
              className="show-mobile"
              aria-label="Open navigation menu"
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{
                width: 40, height: 40,
                alignItems: "center", justifyContent: "center",
                padding: 0, border: "none", background: "transparent",
                color: "var(--text)", display: "none", cursor: "pointer"
              }}
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile search */}
        <div style={{ padding: "0 16px 12px", width: "100%", display: "none", boxSizing: "border-box" }} className="show-mobile">
          <div style={{ width: "100%" }}>
            <SearchBar compact />
          </div>
        </div>

        {/* ── Category Strip (desktop) ───────────────────────────────────── */}
        <div style={{ borderTop: "1px solid var(--border)", overflowX: "auto", scrollbarWidth: "none" }} className="hide-mobile">
          <div style={{ display: "flex", padding: "0 28px", minWidth: "max-content", alignItems: "center" }}>
            {[
              { label: "All Products", href: "/shop" },
              { label: "Computing", href: "/shop?category=Computing" },
              { label: "Electronics", href: "/shop?category=Electronics" },
              { label: "Power & Solar", href: "/shop?category=Power%20%26%20Solar" },
              { label: "Networking", href: "/shop?category=Networking" },
              { label: "Home & Office", href: "/shop?category=Home%20%26%20Office" },
              { label: "Fashion", href: "/shop?category=Fashion" },
              { label: "Beauty & Personal Care", href: "/shop?category=Beauty%20%26%20Personal%20Care" },
              { label: "Kids & Toys", href: "/shop?category=Kids%20%26%20Toys" },
              { label: "Party & Gifts", href: "/shop?category=Party%20%26%20Gifts" },
              { label: "⚡ Solar Calculator", href: "/solar-calculator", highlight: true },
              { label: "About Us", href: "/about" },
            ].map(({ label, href, highlight }) => {
              const isActive = location.pathname + location.search === href || (href === "/shop" && location.pathname === "/shop" && !location.search);
              return (
                <Link key={label} to={href}
                  style={{
                    padding: "9px 14px", fontSize: 13,
                    fontWeight: isActive || highlight ? 700 : 500,
                    color: highlight ? "var(--brand)" : isActive ? "var(--brand)" : "var(--text)",
                    whiteSpace: "nowrap",
                    borderBottom: isActive ? "2px solid var(--brand)" : "2px solid transparent",
                    background: highlight ? "rgba(232, 71, 26, 0.08)" : "transparent",
                    borderRadius: highlight ? "var(--radius-sm)" : "0",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { if (!isActive && !highlight) { e.currentTarget.style.color = "var(--brand)"; e.currentTarget.style.borderBottomColor = "var(--brand)"; } }}
                  onMouseLeave={e => { if (!isActive && !highlight) { e.currentTarget.style.color = "var(--text)"; e.currentTarget.style.borderBottomColor = "transparent"; } }}
                >
                  {label}
                </Link>
              );
            })}
            {compareList.length > 0 && (
              <Link to="/compare"
                style={{ padding: "9px 14px", fontSize: 13, fontWeight: 600, color: "var(--brand)", whiteSpace: "nowrap", borderBottom: "2px solid transparent" }}
              >
                Compare ({compareList.length})
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* ── Mobile Drawer ─────────────────────────────────────────────────── */}
      {mobileOpen && (
        <>
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(13,27,42,0.5)", zIndex: 300, backdropFilter: "blur(4px)" }}
            onClick={() => setMobileOpen(false)}
          />
          <div style={{
            position: "fixed", top: 0, left: 0, bottom: 0, width: 280,
            background: "#fff", zIndex: 400, padding: 0, overflowY: "auto",
            boxShadow: "4px 0 40px rgba(13,27,42,0.2)",
            animation: "slideInLeft 0.28s var(--ease)",
          }}>
            <style>{`@keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>
            <div style={{ padding: 20, borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <img src="/images/Afriportstore.png" alt="Afriport" style={{ height: 38, width: "auto", objectFit: "contain" }} onError={e => { e.currentTarget.style.display = 'none'; }} />
              </div>
              <button onClick={() => setMobileOpen(false)} style={{ border: "none", background: "transparent", padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            {user && (
              <div style={{ padding: "16px 20px", background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg, var(--brand), #FF6B35)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                    {profile?.fullName?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{profile?.fullName}</div>
                    <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: "var(--radius-full)", background: roleConfig.bg, color: roleConfig.color, fontWeight: 600 }}>{roleConfig.label}</span>
                  </div>
                </div>
              </div>
            )}

            <nav style={{ padding: 12 }}>
              {[
                ["/", Home, "Home"],
                ["/shop", Store, "Shop All Products"],
                ["/solar-calculator", Sun, "Solar Calculator"],
                ["/about", Info, "About Us"],
                ["/admin", Settings, "Admin Portal"],
                ...(user ? [
                  ["/account/orders", Package, "My Orders"],
                  ["/wishlist", Heart, "Wishlist"],
                  ["/returns", RotateCcw, "Returns"],
                ] : []),
                ...(profile?.role === "business" ? [["/business/dashboard", Package, "Seller Dashboard"]] : []),
              ].map(([href, Icon, label]) => (
                <Link key={href} to={href} onClick={() => setMobileOpen(false)}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 12px", borderRadius: "var(--radius-sm)", fontSize: 15, fontWeight: 500, color: "var(--text)", transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <Icon size={17} color="var(--text-muted)" />
                  {label}
                </Link>
              ))}

              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
                <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", padding: "0 12px 8px" }}>
                  Categories
                </p>
                {CATEGORY_GROUPS.map(grp => (
                  <Link key={grp.group} to={`/shop?category=${encodeURIComponent(grp.group)}`} onClick={() => setMobileOpen(false)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: "var(--radius-sm)", fontSize: 14, color: "var(--text)", textDecoration: "none", transition: "background 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--surface)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  >
                    <span style={{ fontSize: 16 }}>{grp.icon}</span>
                    <span style={{ fontWeight: 500 }}>{grp.group}</span>
                  </Link>
                ))}
              </div>
            </nav>

            <div style={{ padding: 16, borderTop: "1px solid var(--border)" }}>
              {user ? (
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="btn btn-ghost w-full" style={{ color: "var(--danger)", borderColor: "var(--danger)" }}>
                  <LogOut size={16} /> Sign Out
                </button>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Link to="/login" className="btn btn-ghost w-full" onClick={() => setMobileOpen(false)}>Sign In</Link>
                  <Link to="/register" className="btn btn-primary w-full" onClick={() => setMobileOpen(false)}>Create Account</Link>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}
